import React, { PureComponent } from 'react';
import { BaseInputProps } from '../types';
import classNames from 'classnames';
import { isExpired, isNullLike } from '../utils';
import { Num, NumCvt } from './Num';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { TextSelector } from './TextSelector';
import { capitalize } from 'lodash-es';
import { Bool } from './Bool';
import { Checkbox, DatePicker, Select } from 'antd';
import { NumRange } from './NumRange';
import { NumRangeFromTo } from './NumRangeFromTo';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

interface DeltaRange {
  min?: number;
  max?: number;
}

export interface VegaRule {
  optionType?: string;
  deltaRange: DeltaRange;
  bidEdgeSpan: {
    from?: number;
    to?: number;
  };
  askEdgeSpan: {
    from?: number;
    to?: number;
  };
  allowTaker: boolean;
  tob: boolean;
  disable: boolean;
}

export interface ExpiryRangeVegaAlphaByEdgeInfo {
  startExpiry?: string;
  endExpiry?: string;
  vegaTarget?: number;
  vegaRules: VegaRule[];
}

function hasRangeOverlap(range1: DeltaRange, range2: DeltaRange): boolean {
  const min1 = range1.min ?? -Infinity;
  const max1 = range1.max ?? Infinity;
  const min2 = range2.min ?? -Infinity;
  const max2 = range2.max ?? Infinity;

  // 边界相等不算重叠，所以用严格比较
  return min1 < max2 && min2 < max1;
}

/**
 * 检查VegaRules数组是否合法
 */
export function validateVegaRules(rules: VegaRule[]): boolean {
  for (let i = 0; i < rules.length; i++) {
    const currentRule = rules[i];
    const currentType = currentRule.optionType ?? 'Both'; // 默认按Both处理

    for (let j = i + 1; j < rules.length; j++) {
      const compareRule = rules[j];
      const compareType = compareRule.optionType ?? 'Both';

      // 检查是否需要比较这两个规则
      let shouldCompare = false;

      if (currentType === 'Both' || compareType === 'Both') {
        // 任意一个是Both，就需要比较
        shouldCompare = true;
      } else if (currentType === compareType) {
        // 类型相同（都是Call或都是Put），需要比较
        shouldCompare = true;
      }
      // 类型不同（一个Call一个Put），不需要比较

      if (shouldCompare && hasRangeOverlap(currentRule.deltaRange, compareRule.deltaRange)) {
        return false; // 发现重叠，不合法
      }
    }
  }

  return true; // 没有发现重叠，合法
}

export interface ExpiryRangeVegaAlphaByEdgeProps extends BaseInputProps {
  value?: ExpiryRangeVegaAlphaByEdgeInfo[];

  onChange?: (val: ExpiryRangeVegaAlphaByEdgeInfo[]) => void;

  options?: string[];
}

export class ExpiryRangeVegaAlphaByEdgeList extends PureComponent<ExpiryRangeVegaAlphaByEdgeProps> {
  value: ExpiryRangeVegaAlphaByEdgeInfo[] = [];

  /**
   * Convert date string from format "27NOV24" to dayjs object
   */
  parseExpiryDate = (expiry: string): Dayjs | null => {
    if (!expiry) return null;

    const parts = expiry.match(/\d+|[a-zA-Z]+/g);
    if (!parts || parts.length < 3) return null;

    const day = parseInt(parts[0], 10);
    const monthStr = parts[1].toUpperCase();
    const year = parseInt(parts[2], 10);

    // Map month abbreviations to month numbers (0-indexed)
    const monthMap: Record<string, number> = {
      JAN: 0,
      FEB: 1,
      MAR: 2,
      APR: 3,
      MAY: 4,
      JUN: 5,
      JUL: 6,
      AUG: 7,
      SEP: 8,
      OCT: 9,
      NOV: 10,
      DEC: 11,
    };

    const month = monthMap[monthStr];
    if (month === undefined) return null;

    // Assume year is 20XX
    const fullYear = 2000 + year;

    // Create dayjs object using the Date constructor
    const date = dayjs(new Date(fullYear, month, day));

    return date.isValid() ? date : null;
  };

  /**
   * Convert dayjs object to format "27NOV24" or "3DEC24"
   */
  formatExpiryDate = (date: Dayjs): string => {
    const day = date.date();
    const month = date.format('MMM').toUpperCase();
    const year = date.format('YY');
    return `${day}${month}${year}`;
  };

  /**
   * Disable dates that are before today or already selected by other items
   */
  getDisabledDate =
    (currentIndex: number) =>
    (current: Dayjs): boolean => {
      if (!current) return true;

      // Disable dates before today
      const today = dayjs().startOf('day');
      if (current.isBefore(today, 'day')) {
        return true;
      }

      // Check if already selected by other items
      const isAlreadySelected = this.value.some((vega, index) => {
        if (index === currentIndex) return false;

        const start = vega.startExpiry;
        const end = vega.endExpiry;

        // 如果开始和结束都没有设置,跳过这一行
        if (!start && !end) return false;

        const currentDate = current.valueOf();
        const startDate = start ? this.parseExpiryDate(start)?.valueOf() : undefined;
        const endDate = end ? this.parseExpiryDate(end)?.valueOf() : undefined;

        // 如果只有开始日期,检查是否等于开始日期
        if (startDate && !endDate) {
          return currentDate === startDate;
        }

        // 如果只有结束日期,检查是否等于结束日期
        if (!startDate && endDate) {
          return currentDate === endDate;
        }

        // 如果都有,检查是否在范围内
        if (startDate && endDate) {
          return currentDate >= startDate && currentDate <= endDate;
        }

        return false;
      });

      return isAlreadySelected;
    };

  isEmptyItem = (item: ExpiryRangeVegaAlphaByEdgeInfo) => {
    const vegaRulesValid = item.vegaRules
      ? item.vegaRules?.find(
          (it) =>
            it.deltaRange.min ||
            it.deltaRange.max ||
            it.bidEdgeSpan.from ||
            it.bidEdgeSpan.to ||
            it.askEdgeSpan.from ||
            it.askEdgeSpan.to
        )
      : true;
    return (
      !item.startExpiry?.trim() && !item.endExpiry?.trim() && !item.vegaTarget && !vegaRulesValid
    );
  };

  onChange = (
    i: number,
    val?: any,
    key?: keyof ExpiryRangeVegaAlphaByEdgeInfo,
    k?: number,
    subKey?: keyof VegaRule
  ) => {
    const value = [...this.value];
    if (!value[i])
      value[i] = {
        startExpiry: undefined,
        endExpiry: undefined,
        vegaTarget: undefined,
        vegaRules: [
          {
            optionType: undefined,
            deltaRange: {
              min: undefined,
              max: undefined,
            },
            bidEdgeSpan: {
              from: undefined,
              to: undefined,
            },
            askEdgeSpan: {
              from: undefined,
              to: undefined,
            },
            allowTaker: false,
            tob: false,
            disable: false,
          },
        ],
      };

    if (key) {
      if (subKey && typeof k === 'number') {
        // if (subKey === 'optionType') {
        //   // console.log('changeValue==========================', value, i, val, key, k, subKey);

        //   if (val === 'Both') {
        //     value[i].vegaRules = [{ ...value[i].vegaRules[k], optionType: 'Both' }];
        //   } else {
        //     if (value[i].vegaRules?.length <= 1)
        //       value[i].vegaRules = [
        //         { ...value[i].vegaRules[k], optionType: val },
        //         {
        //           optionType: val === 'Call' ? 'Put' : 'Call',
        //           deltaRange: {
        //             min: undefined,
        //             max: undefined,
        //           },
        //           bidEdgeSpan: {
        //             from: undefined,
        //             to: undefined,
        //           },
        //           askEdgeSpan: {
        //             from: undefined,
        //             to: undefined,
        //           },
        //           allowTaker: false,
        //           disable: false,
        //         },
        //       ];
        //     else value[i].vegaRules[k][subKey] = val as never;
        //   }
        // } else {
        //   value[i].vegaRules[k][subKey] = val as never;
        // }
        value[i].vegaRules[k][subKey] = val as never;
      } else {
        value[i][key] = val as never;
      }
    } else {
      value[i] = { ...value[i], ...val };
    }

    this.props.onChange?.(value.length === 1 && this.isEmptyItem(value[0]) ? [] : value);
  };

  onAdd = (i: number) => {
    const value = [...this.value];
    value.splice(i + 1, 0, {
      startExpiry: undefined,
      endExpiry: undefined,
      vegaTarget: undefined,
      vegaRules: [
        {
          optionType: undefined,
          deltaRange: {
            min: undefined,
            max: undefined,
          },
          bidEdgeSpan: {
            from: undefined,
            to: undefined,
          },
          askEdgeSpan: {
            from: undefined,
            to: undefined,
          },
          allowTaker: false,
          tob: false,
          disable: false,
        },
      ],
    });
    this.props.onChange?.(value);
    this.props.onBlur?.();
  };
  onAddRule = (i: number, k: number) => {
    const value = [...this.value];
    value[i].vegaRules.splice(k + 1, 0, {
      optionType: undefined,
      deltaRange: {
        min: undefined,
        max: undefined,
      },
      bidEdgeSpan: {
        from: undefined,
        to: undefined,
      },
      askEdgeSpan: {
        from: undefined,
        to: undefined,
      },
      allowTaker: false,
      tob: false,
      disable: false,
    });
    this.props.onChange?.(value);
    this.props.onBlur?.();
  };

  onDel = (i: number) => {
    const value = [...this.value];
    value.splice(i, 1);
    this.props.onChange?.(value.length === 1 && this.isEmptyItem(value[0]) ? [] : value);
    this.props.onBlur?.();
  };

  onDelRule = (i: number, k: number) => {
    const value = [...this.value];
    value[i].vegaRules.splice(k, 1);
    this.props.onChange?.(value.length === 1 && this.isEmptyItem(value[0]) ? [] : value);
    this.props.onBlur?.();
  };

  render() {
    this.value =
      this.props.value?.filter((it) => !isExpired(it.startExpiry) && !isExpired(it.endExpiry)) ||
      [];
    if (!this.value.length)
      this.value.push({
        startExpiry: undefined,
        endExpiry: undefined,
        vegaTarget: undefined,
        vegaRules: [
          {
            optionType: undefined,
            deltaRange: {
              min: undefined,
              max: undefined,
            },
            bidEdgeSpan: {
              from: undefined,
              to: undefined,
            },
            askEdgeSpan: {
              from: undefined,
              to: undefined,
            },
            allowTaker: false,
            tob: false,
            disable: false,
          },
        ],
      });
    return (
      <div className={classNames(this.props.className)} style={this.props.style}>
        {this.props.readonly ? (
          this.props.value?.length ? (
            this.value
              .filter((it) => it.startExpiry || it.endExpiry)
              .map((it, i) => (
                <>
                  <span key={i}>
                    {it.startExpiry || '-'} ~ {it.endExpiry || '-'}
                    <span>/</span>
                    {!isNullLike(it.vegaTarget) ? it.vegaTarget : '-'}
                    <span>/</span>
                    {it.vegaRules.map(($it, $i) => (
                      <>
                        <span key={$i}>
                          {!isNullLike($it.optionType) ? $it.optionType : '-'}
                          <span>/</span>
                          {!isNullLike($it.deltaRange.min) && !isNullLike($it.deltaRange.max)
                            ? `(${$it.deltaRange.min}, ${$it.deltaRange.max})`
                            : '-'}
                          <span>/</span>
                          {!isNullLike($it.bidEdgeSpan.from) && !isNullLike($it.bidEdgeSpan.to)
                            ? `(${NumCvt.show($it.bidEdgeSpan.from, true)}%, ${NumCvt.show(
                                $it.bidEdgeSpan.to,
                                true
                              )}%)`
                            : '-'}
                          <span>/</span>
                          {!isNullLike($it.askEdgeSpan.from) && !isNullLike($it.askEdgeSpan.to)
                            ? `(${NumCvt.show($it.askEdgeSpan.from, true)}%, ${NumCvt.show(
                                $it.askEdgeSpan.to,
                                true
                              )}%)`
                            : '-'}
                          <span>/</span>
                          {typeof $it.allowTaker === 'boolean'
                            ? $it.allowTaker.toString().toUpperCase()
                            : '-'}
                          <span>/</span>
                          {typeof $it.tob === 'boolean' ? $it.tob.toString().toUpperCase() : '-'}
                          <span>/</span>
                          {typeof $it.disable === 'boolean'
                            ? $it.disable.toString().toUpperCase()
                            : '-'}
                        </span>
                        <br />
                      </>
                    ))}
                  </span>
                  <br />
                </>
              ))
          ) : (
            '-'
          )
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: 1150 }}>
            {/* 表头 */}
            <div style={{ display: 'flex', width: '100%', gap: 8, alignItems: 'center', color: '#374151', fontWeight: 500 }}>
              <div style={{ width: 210 }}>Expiry Range</div>
              <div style={{ width: 80 }}>Vega Target</div>
              {/* Vega Rules 表头区域 - 匹配内容结构 */}
              <div style={{ flex: 1, minWidth: 734 }}>
                <div style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 120, display: 'flex', alignItems: 'center' }}>
                    <div style={{ width: 18 }}></div>
                    <div style={{ width: 100 }}>Both/Call/Put</div>
                  </div>
                  <div style={{ width: 160 }}>Delta Range</div>
                  <div style={{ width: 200 }}>Bid Edge Range</div>
                  <div style={{ width: 200 }}>Ask Edge Range</div>
                  <div style={{ width: 74, textAlign: 'center' }}>Allow Taker</div>
                  <div style={{ width: 50, textAlign: 'center' }}>TOB</div>
                  <div style={{ width: 60, textAlign: 'center' }}>Disable</div>
                </div>
              </div>
              {/* 操作按钮占位 */}
              <div style={{ width: 40 }}></div>
            </div>

            {this.value.map((it, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                }}
              >
                {/* 主内容行 */}
                <div style={{ display: 'flex', width: '100%', alignItems: 'flex-start', gap: 8 }}>
                  {/* Expiry Range 输入 */}
                  <div style={{ width: 210, marginRight: 0 }}>
                    <DatePicker.RangePicker
                      style={{ width: 210 }}
                      value={(() => {
                        const start = this.parseExpiryDate(
                          this.props.value?.[i]?.startExpiry || ''
                        );
                        const end = this.parseExpiryDate(this.props.value?.[i]?.endExpiry || '');
                        return start || end ? [start, end] : null;
                      })()}
                      onChange={(dates) => {
                        const value = [...this.value];
                        if (!value[i]) {
                          value[i] = {
                            startExpiry: undefined,
                            endExpiry: undefined,
                            vegaTarget: undefined,
                            vegaRules: [
                              {
                                optionType: undefined,
                                deltaRange: { min: undefined, max: undefined },
                                bidEdgeSpan: { from: undefined, to: undefined },
                                askEdgeSpan: { from: undefined, to: undefined },
                                allowTaker: false,
                                tob: false,
                                disable: false,
                              },
                            ],
                          };
                        }
                        if (dates) {
                          const [start, end] = dates;
                          value[i].startExpiry = start ? this.formatExpiryDate(start) : undefined;
                          value[i].endExpiry = end ? this.formatExpiryDate(end) : undefined;
                        } else {
                          value[i].startExpiry = undefined;
                          value[i].endExpiry = undefined;
                        }
                        this.props.onChange?.(
                          value.length === 1 && this.isEmptyItem(value[0]) ? [] : value
                        );
                      }}
                      onBlur={this.props.onBlur}
                      disabledDate={this.getDisabledDate(i)}
                      format={(value) => (value ? this.formatExpiryDate(value) : '')}
                      placeholder={['Start', 'End']}
                      allowClear
                    />
                  </div>

                  {/* Vega Target 输入 */}
                  <div style={{ width: 80 }}>
                    <Num
                      value={this.props.value?.[i]?.vegaTarget}
                      onChange={(val) => this.onChange(i, val, 'vegaTarget')}
                      isPercentage={false}
                      readonly={this.props.readonly}
                      onBlur={this.props.onBlur}
                      style={{ height: 30 }}
                      singleWidth={50}
                    />
                  </div>

                  {/* Vega Rules 区域 */}
                  <div style={{ flex: 1, minWidth: 734 }}>
                    {/* Rules 内容 */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {it.vegaRules.map((rule, k) => (
                        <div
                          key={k}
                          style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 8 }}
                        >
                          {/* Option Type */}
                          <div
                            style={{
                              display: 'flex',
                              width: 120,
                              height: 30,
                              alignItems: 'center',
                            }}
                          >
                            <div
                              style={{
                                visibility: k === it.vegaRules?.length - 1 ? 'visible' : 'hidden',
                              }}
                            >
                              <PlusOutlined
                                onClick={this.onAddRule.bind(null, i, k)}
                                style={{ width: 18 }}
                              />
                            </div>
                            <Select
                              style={{ width: 100 }}
                              value={[rule.optionType || '']}
                              onChange={(val) =>
                                this.onChange(i, val, 'vegaRules', k, 'optionType')
                              }
                              placeholder="Choose"
                              options={['Both', 'Call', 'Put'].map((it) => ({
                                label: it,
                                value: it,
                              }))}
                            />
                          </div>

                          {/* Delta Range */}
                          <div style={{ width: 160 }}>
                            <NumRange
                              value={{ min: rule.deltaRange.min, max: rule.deltaRange.max }}
                              onChange={(val) =>
                                this.onChange(i, val, 'vegaRules', k, 'deltaRange')
                              }
                              onBlur={this.props.onBlur}
                              singleWidth={50}
                            />
                          </div>

                          {/* Bid Edge Range */}
                          <div style={{ width: 200 }}>
                            <NumRangeFromTo
                              value={{ from: rule.bidEdgeSpan.from, to: rule.bidEdgeSpan.to }}
                              onChange={(val: any) =>
                                this.onChange(i, val, 'vegaRules', k, 'bidEdgeSpan')
                              }
                              isPercentage
                              onBlur={this.props.onBlur}
                            />
                          </div>

                          {/* Ask Edge Range */}
                          <div style={{ width: 200 }}>
                            <NumRangeFromTo
                              value={{ from: rule.askEdgeSpan.from, to: rule.askEdgeSpan.to }}
                              onChange={(val: any) =>
                                this.onChange(i, val, 'vegaRules', k, 'askEdgeSpan')
                              }
                              isPercentage
                              onBlur={this.props.onBlur}
                            />
                          </div>

                          {/* Allow Taker */}
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'center',
                              width: 74,
                            }}
                          >
                            <Checkbox
                              checked={rule.allowTaker}
                              onChange={(e) =>
                                this.onChange(i, e.target.checked, 'vegaRules', k, 'allowTaker')
                              }
                            />
                          </div>

                          {/* TOB */}
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'center',
                              width: 50,
                            }}
                          >
                            <Checkbox
                              checked={rule.tob}
                              onChange={(e) =>
                                this.onChange(i, e.target.checked, 'vegaRules', k, 'tob')
                              }
                            />
                          </div>

                          {/* Disabled + Delete */}
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'center',
                              position: 'relative',
                              width: 60,
                            }}
                          >
                            <Checkbox
                              checked={rule.disable}
                              onChange={(e) =>
                                this.onChange(i, e.target.checked, 'vegaRules', k, 'disable')
                              }
                            />
                            <div
                              style={{
                                position: 'absolute',
                                right: -48,
                                top: -4,
                                visibility: k === 0 ? 'hidden' : 'visible',
                              }}
                            >
                              <DeleteOutlined onClick={this.onDelRule.bind(null, i, k)} />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* 操作按钮 */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      width: 40,
                      marginTop: 10,
                    }}
                  >
                    <PlusOutlined onClick={this.onAdd.bind(null, i)} />
                    <DeleteOutlined onClick={this.onDel.bind(null, i)} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
}
